import {
  getTrustFactLabel,
  ORGANIZATION_TRUST,
  TRUST_FACTS,
} from "../components/trustData";
import { CURRENCY_BY_MARKET, PRICE_TABLE } from "../context/pricing/prices";
import { bookingNicheBySlug } from "../pages/booking/nichesData";
import { industrySeoBySlug } from "./industrySeoData";

export type SupportedLanguage = "en" | "da" | "sv";

type TranslationNamespace =
  | "about"
  | "booking"
  | "case_armring"
  | "case_diagram_editor"
  | "case_mce_it"
  | "case_mkcosmetics"
  | "cases"
  | "contact"
  | "customSolutions"
  | "header"
  | "home"
  | "mobileApps"
  | "onboard"
  | "servicesOverview"
  | "trust"
  | "webshop"
  | "websites";

export type StructuredDataTranslator = <T = string>(
  namespace: TranslationNamespace,
  key: string,
  options?: Record<string, string | boolean>
) => T;

export type JsonLdValue =
  | string
  | number
  | boolean
  | null
  | JsonLdValue[]
  | { [key: string]: JsonLdValue };

export type JsonLdObject = { [key: string]: JsonLdValue };

export type StructuredDataEntry = {
  id: string;
  data: JsonLdObject;
};

type BreadcrumbItem = {
  name: string;
  segments?: string[];
};

type PageMeta = {
  routeKey: string;
  url: string;
  name: string;
  description: string;
  image?: string;
  breadcrumb?: BreadcrumbItem[];
  includeLocalBusiness?: boolean;
  service?: ServiceSchemaMeta;
  faq?: FaqSchemaItem[];
};

type FaqSchemaItem = {
  q: string;
  a: string;
};

type ServiceSlug =
  | "websites"
  | "booking"
  | "webshop"
  | "mobile-apps"
  | "custom-solutions";

type ServiceSchemaMeta = {
  slug: ServiceSlug;
  name: string;
  description: string;
};

const SITE_URL = "https://nalosoft.com";
const ORGANIZATION_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const LOCAL_BUSINESS_ID = `${SITE_URL}/#local-business`;

const BUSINESS_DETAILS = {
  name: "Nalosoft",
  email: "contact@nalosoft.com",
  telephone: "+45 41 81 91 31",
  logo: "/logo.png",
  address: {
    streetAddress: "Korskildelund 6",
    postalCode: "2670",
    addressLocality: "Greve",
    addressCountry: "DK",
  },
  sameAs: [
    "https://www.facebook.com/nalosoft",
    "https://www.instagram.com/nalosoftcom",
    "https://x.com/nalosoft",
  ],
  mapUrl:
    "https://www.google.com/maps/search/?api=1&query=Korskildelund%206%2C%202670%20Greve%2C%20Denmark",
};

const SERVICE_PRICING: Record<
  ServiceSlug,
  | {
      kind: "package";
      setupKey: keyof (typeof PRICE_TABLE)["DK"];
      subscriptionKey: keyof (typeof PRICE_TABLE)["DK"];
    }
  | { kind: "custom"; hourlyKey: keyof (typeof PRICE_TABLE)["DK"] }
> = {
  "websites": {
    kind: "package",
    setupKey: "websitesSetup",
    subscriptionKey: "websitesSubscription",
  },
  "booking": {
    kind: "package",
    setupKey: "bookingSetup",
    subscriptionKey: "bookingSubscription",
  },
  "webshop": {
    kind: "package",
    setupKey: "webshopSetup",
    subscriptionKey: "webshopSubscription",
  },
  "mobile-apps": {
    kind: "package",
    setupKey: "mobileAppSetup",
    subscriptionKey: "mobileAppSubscription",
  },
  "custom-solutions": {
    kind: "custom",
    hourlyKey: "customHourly",
  },
};

const marketByLanguage = {
  en: "INTL",
  da: "DK",
  sv: "SE",
} as const;

export const isSupportedStructuredDataLanguage = (
  value: string | undefined
): value is SupportedLanguage =>
  value === "en" || value === "da" || value === "sv";

export function buildStructuredData(
  pathname: string,
  lng: SupportedLanguage,
  t: StructuredDataTranslator
): StructuredDataEntry[] {
  const page = resolvePageMeta(pathname, lng, t);

  if (!page) {
    return [];
  }

  const entries: StructuredDataEntry[] = [];

  if (page.routeKey === "home") {
    entries.push({ id: "organization", data: buildOrganizationSchema(t) });
  }

  if (page.includeLocalBusiness) {
    entries.push({ id: "local-business", data: buildLocalBusinessSchema() });
  }

  if (page.service) {
    entries.push({ id: "service", data: buildServiceSchema(page, lng) });
  }

  entries.push({
    id: "webpage",
    data: buildWebPageSchema(page, lng),
  });

  if (page.breadcrumb) {
    entries.push({
      id: "breadcrumb",
      data: buildBreadcrumbSchema(page.url, lng, page.breadcrumb),
    });
  }

  if (page.faq?.length) {
    entries.push({ id: "faq", data: buildFaqSchema(page.url, page.faq) });
  }

  return entries;
}

export function serializeJsonLd(schema: JsonLdObject) {
  return JSON.stringify(schema).replace(/</g, "\\u003c");
}

function resolvePageMeta(
  pathname: string,
  lng: SupportedLanguage,
  t: StructuredDataTranslator
): PageMeta | null {
  const segments = getRouteSegments(pathname);
  const tr = (namespace: TranslationNamespace, key: string) =>
    cleanText(String(t(namespace, key)));

  if (segments.length === 0) {
    return {
      routeKey: "home",
      url: localizedUrl(lng),
      name: joinText([
        tr("home", "hero.titleLine1"),
        tr("home", "hero.titleLine2"),
      ]),
      description: tr("home", "hero.desc"),
      image: "/misc/homepage-office.jpg",
    };
  }

  const [section, slug] = segments;

  if (section === "about" && !slug) {
    return {
      routeKey: "about",
      url: localizedUrl(lng, ["about"]),
      name: joinText([
        tr("about", "hero.titleLine1"),
        tr("about", "hero.titleLine2"),
      ]),
      description: tr("about", "hero.intro1"),
      image: "/misc/about-photo.jpg",
    };
  }

  if (section === "contact" && !slug) {
    return {
      routeKey: "contact",
      url: localizedUrl(lng, ["contact"]),
      name: tr("contact", "hero.title"),
      description: tr("contact", "hero.desc"),
      includeLocalBusiness: true,
    };
  }

  if ((section === "onboard" || section === "schedule") && !slug) {
    return {
      routeKey: section,
      url: localizedUrl(lng, [section]),
      name: tr("onboard", "hero.title"),
      description: tr("onboard", "hero.desc"),
    };
  }

  if (section === "cases") {
    return resolveCasePageMeta(slug, lng, tr);
  }

  if (section === "services") {
    return slug
      ? resolveServicePageMeta(slug, lng, tr, t)
      : {
          routeKey: "services",
          url: localizedUrl(lng, ["services"]),
          name: tr("servicesOverview", "hero.title"),
          description: tr("servicesOverview", "hero.subtitle"),
          image: "/services/websites-service.jpg",
          breadcrumb: [
            homeBreadcrumb(lng, tr),
            {
              name: tr("servicesOverview", "comparison.heading"),
              segments: ["services"],
            },
          ],
        };
  }

  if (section === "booking" && slug && lng === "da") {
    const bookingNiche = bookingNicheBySlug[slug];

    if (!bookingNiche) {
      return null;
    }

    const segments = ["booking", slug];

    return {
      routeKey: `booking-niche-${slug}`,
      url: localizedUrl(lng, segments),
      name: bookingNiche.h1Heading,
      description: bookingNiche.metaDescription,
      image: bookingNiche.image,
      service: {
        slug: "booking",
        name: bookingNiche.h1Heading,
        description: bookingNiche.metaDescription,
      },
      faq: bookingNiche.faq,
      breadcrumb: [
        homeBreadcrumb(lng, tr),
        { name: "Booking", segments: ["services", "booking"] },
        { name: bookingNiche.h1Heading, segments },
      ],
    };
  }

  if (section === "brancher" && slug && lng === "da") {
    const industryPage = industrySeoBySlug[slug];

    if (!industryPage) {
      return null;
    }

    const segments = ["brancher", slug];

    return {
      routeKey: `industry-${slug}`,
      url: localizedUrl(lng, segments),
      name: industryPage.heroTitle,
      description: industryPage.seo.description,
      image: industryPage.seo.image,
      faq: industryPage.faq,
      breadcrumb: [
        homeBreadcrumb(lng, tr),
        { name: "Brancher" },
        { name: industryPage.heroTitle, segments },
      ],
    };
  }

  return null;
}

function resolveServicePageMeta(
  slug: string,
  lng: SupportedLanguage,
  tr: (namespace: TranslationNamespace, key: string) => string,
  t: StructuredDataTranslator
): PageMeta | null {
  const services = {
    "websites": {
      namespace: "websites",
      routeKey: "services-websites",
    },
    "booking": {
      namespace: "booking",
      routeKey: "services-booking",
    },
    "webshop": {
      namespace: "webshop",
      routeKey: "services-webshop",
    },
    "mobile-apps": {
      namespace: "mobileApps",
      routeKey: "services-mobile-apps",
    },
    "custom-solutions": {
      namespace: "customSolutions",
      routeKey: "services-custom-solutions",
    },
  } as const;

  const service = services[slug as keyof typeof services];

  if (!service) {
    return null;
  }

  const namespace = service.namespace;
  const segments = ["services", slug];
  const name = tr(namespace, "hero.title");
  const description = tr(namespace, "hero.subtitle");

  return {
    routeKey: service.routeKey,
    url: localizedUrl(lng, segments),
    name,
    description,
    image: serviceImage(slug),
    service: {
      slug: slug as ServiceSlug,
      name,
      description,
    },
    faq: readFaqItems(t, namespace),
    breadcrumb: [
      homeBreadcrumb(lng, tr),
      { name: tr("header", "nav.services") },
      { name, segments },
    ],
  };
}

function resolveCasePageMeta(
  slug: string | undefined,
  lng: SupportedLanguage,
  tr: (namespace: TranslationNamespace, key: string) => string
): PageMeta | null {
  if (!slug) {
    return {
      routeKey: "cases",
      url: localizedUrl(lng, ["cases"]),
      name: tr("cases", "hero.title"),
      description: tr("cases", "hero.subtitle"),
      image: "/services/cases-1.jpg",
      breadcrumb: [
        homeBreadcrumb(lng, tr),
        { name: tr("cases", "hero.title"), segments: ["cases"] },
      ],
    };
  }

  const cases = {
    "armring": {
      namespace: "case_armring",
      routeKey: "case-armring",
    },
    "mkcosmetics": {
      namespace: "case_mkcosmetics",
      routeKey: "case-mkcosmetics",
    },
    "mce-it": {
      namespace: "case_mce_it",
      routeKey: "case-mce-it",
    },
    "diagram-editor": {
      namespace: "case_diagram_editor",
      routeKey: "case-diagram-editor",
    },
  } as const;

  const casePage = cases[slug as keyof typeof cases];

  if (!casePage) {
    return null;
  }

  const namespace = casePage.namespace;
  const segments = ["cases", slug];
  const name = tr(namespace, "hero.title");

  return {
    routeKey: casePage.routeKey,
    url: localizedUrl(lng, segments),
    name,
    description: tr(namespace, "hero.description"),
    image: tr(namespace, "hero.heroImage"),
    breadcrumb: [
      homeBreadcrumb(lng, tr),
      { name: tr("cases", "hero.title"), segments: ["cases"] },
      { name, segments },
    ],
  };
}

function buildOrganizationSchema(t: StructuredDataTranslator): JsonLdObject {
  const trustT = (key: string, options?: Record<string, string>) =>
    cleanText(String(t("trust", key, options)));
  const trustFacts = TRUST_FACTS.map((fact) =>
    getTrustFactLabel(trustT, fact.id)
  );

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: ORGANIZATION_TRUST.name,
    url: `${ORGANIZATION_TRUST.url}/`,
    logo: ORGANIZATION_TRUST.logoUrl,
    foundingDate: ORGANIZATION_TRUST.foundingYear,
    taxID: `CVR ${ORGANIZATION_TRUST.cvrNumber}`,
    vatID: ORGANIZATION_TRUST.vatId,
    identifier: {
      "@type": "PropertyValue",
      propertyID: "CVR",
      value: ORGANIZATION_TRUST.cvrNumber,
    },
    description: trustT("jsonLd.description", {
      year: ORGANIZATION_TRUST.foundingYear,
    }),
    areaServed: {
      "@type": "Country",
      name: "Denmark",
    },
    knowsAbout: trustFacts,
    makesOffer: [
      {
        "@type": "Offer",
        name: getTrustFactLabel(trustT, "fixedScope"),
      },
      {
        "@type": "Offer",
        name: getTrustFactLabel(trustT, "care"),
      },
    ],
    email: BUSINESS_DETAILS.email,
    telephone: BUSINESS_DETAILS.telephone,
    contactPoint: buildContactPointSchema(),
    sameAs: BUSINESS_DETAILS.sameAs,
  };
}

function buildLocalBusinessSchema(): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": LOCAL_BUSINESS_ID,
    name: BUSINESS_DETAILS.name,
    url: `${SITE_URL}/`,
    logo: absoluteUrl(BUSINESS_DETAILS.logo),
    image: absoluteUrl(BUSINESS_DETAILS.logo),
    email: BUSINESS_DETAILS.email,
    telephone: BUSINESS_DETAILS.telephone,
    foundingDate: ORGANIZATION_TRUST.foundingYear,
    taxID: `CVR ${ORGANIZATION_TRUST.cvrNumber}`,
    vatID: ORGANIZATION_TRUST.vatId,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      ...BUSINESS_DETAILS.address,
    },
    areaServed: [
      { "@type": "Country", name: "Denmark" },
      { "@type": "Country", name: "Sweden" },
      { "@type": "Country", name: "International" },
    ],
    contactPoint: buildContactPointSchema(),
    hasMap: BUSINESS_DETAILS.mapUrl,
    sameAs: BUSINESS_DETAILS.sameAs,
  };
}

function buildWebPageSchema(page: PageMeta, lng: SupportedLanguage): JsonLdObject {
  const schema: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${page.url}#webpage`,
    url: page.url,
    name: page.name,
    description: page.description,
    inLanguage: lng,
    isPartOf: {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      name: BUSINESS_DETAILS.name,
      url: `${SITE_URL}/`,
    },
    publisher: {
      "@id": ORGANIZATION_ID,
    },
  };

  if (page.image) {
    schema.primaryImageOfPage = {
      "@type": "ImageObject",
      url: absoluteUrl(page.image),
    };
  }

  if (page.breadcrumb) {
    schema.breadcrumb = {
      "@id": `${page.url}#breadcrumb`,
    };
  }

  if (page.service) {
    schema.mainEntity = {
      "@id": `${page.url}#service`,
    };
  }

  return schema;
}

function buildServiceSchema(page: PageMeta, lng: SupportedLanguage): JsonLdObject {
  if (!page.service) {
    throw new Error(`Missing service metadata for ${page.url}`);
  }

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${page.url}#service`,
    name: page.service.name,
    serviceType: page.service.name,
    description: page.service.description,
    url: page.url,
    provider: {
      "@id": ORGANIZATION_ID,
    },
    areaServed: [
      {
        "@type": "Country",
        name:
          lng === "sv" ? "Sweden" : lng === "da" ? "Denmark" : "International",
      },
    ],
    offers: buildServiceOffers(page.service.slug, lng, page.url),
  };
}

function buildServiceOffers(
  slug: ServiceSlug,
  lng: SupportedLanguage,
  url: string
): JsonLdObject {
  const pricing = SERVICE_PRICING[slug];
  const market = marketByLanguage[lng];
  const currency = CURRENCY_BY_MARKET[market].code;

  const itemListElement =
    pricing.kind === "package"
      ? [
          buildOffer("Setup", PRICE_TABLE[market][pricing.setupKey], currency, url),
          buildOffer(
            "Monthly operations",
            PRICE_TABLE[market][pricing.subscriptionKey],
            currency,
            url
          ),
        ]
      : [
          buildOffer(
            "Hourly custom software work",
            PRICE_TABLE[market][pricing.hourlyKey],
            currency,
            url
          ),
        ];

  return {
    "@type": "OfferCatalog",
    name: "Nalosoft service pricing",
    itemListElement,
  };
}

function buildOffer(
  name: string,
  price: number,
  currency: string,
  url: string
): JsonLdObject {
  return {
    "@type": "Offer",
    name,
    price,
    priceCurrency: currency,
    url,
    availability: "https://schema.org/InStock",
    seller: {
      "@id": ORGANIZATION_ID,
    },
  };
}

function buildFaqSchema(pageUrl: string, faq: FaqSchemaItem[]): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${pageUrl}#faq`,
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: cleanText(item.q),
      acceptedAnswer: {
        "@type": "Answer",
        text: cleanText(item.a),
      },
    })),
  };
}

function buildBreadcrumbSchema(
  pageUrl: string,
  lng: SupportedLanguage,
  items: BreadcrumbItem[]
): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumb`,
    itemListElement: items.map((item, index) => {
      const listItem: JsonLdObject = {
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
      };

      if (item.segments) {
        listItem.item = localizedUrl(lng, item.segments);
      }

      return listItem;
    }),
  };
}

function localizedUrl(lng: SupportedLanguage, segments: string[] = []) {
  return `${SITE_URL}/${[lng, ...segments].join("/")}`;
}

function homeBreadcrumb(
  lng: SupportedLanguage,
  tr: (namespace: TranslationNamespace, key: string) => string
): BreadcrumbItem {
  return {
    name: tr("header", "nav.home"),
    segments: [],
  };
}

function absoluteUrl(pathOrUrl: string) {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  return `${SITE_URL}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

function buildContactPointSchema(): JsonLdObject {
  return {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: BUSINESS_DETAILS.email,
    telephone: BUSINESS_DETAILS.telephone,
    availableLanguage: ["English", "Danish", "Swedish"],
  };
}

function readFaqItems(
  t: StructuredDataTranslator,
  namespace: TranslationNamespace
): FaqSchemaItem[] | undefined {
  const value = t<unknown>(namespace, "faq.items", { returnObjects: true });

  if (!Array.isArray(value)) return undefined;

  const items = value
    .map((item) => {
      if (!isRecord(item)) return null;
      const q = typeof item.q === "string" ? cleanText(item.q) : "";
      const a = typeof item.a === "string" ? cleanText(item.a) : "";
      return q && a ? { q, a } : null;
    })
    .filter((item): item is FaqSchemaItem => item !== null);

  return items.length ? items : undefined;
}

function getRouteSegments(pathname: string) {
  const [pathOnly] = pathname.split(/[?#]/);
  const segments = pathOnly.split("/").filter(Boolean);

  if (isSupportedStructuredDataLanguage(segments[0])) {
    return segments.slice(1);
  }

  return segments;
}

function serviceImage(slug: string) {
  switch (slug) {
    case "booking":
      return "/cases/mkcosmetics/mkcosmetics-booking1.png";
    case "custom-solutions":
      return "/services/custom-web-apps-1.jpg";
    case "mobile-apps":
      return "/services/mobile-apps-1.jpg";
    case "webshop":
      return "/cases/armring/armring-homepage.png";
    case "websites":
      return "/services/websites-service.jpg";
    default:
      return undefined;
  }
}

function cleanText(value: string) {
  return value.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}

function joinText(parts: string[]) {
  return cleanText(parts.filter(Boolean).join(" "));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
